import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { CourseEditTime } from "@shared/models/analytics/course"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Course, CourseCalendarDate, CourseCalendarTime } from "@shared/models/course"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "../../../action-doc-builder"
import { partial } from "lodash"
import moment from "moment"
import type { Narrative } from "../../create-narrative"
import defaultCourseActivity from "../default-course-activity"
import defaultCourseContext from "../default-course-context"
import defaultCourseModels from "../default-course-models"

interface Payload {
  course: Course
  courseDate: CourseCalendarDate
  rotationTitle?: string
  planbookId: string
  time: CourseCalendarTime | null
}

export function prepareCourseSetOneTimeAction(payload: Payload): PreparedAction {
  return {
    name: "COURSE_SET_ONE_TIME",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload): Array<PatchModel> {
  return [
    Patch.create("course", payload.course.id, "attributes.calendar.dates", {
      id: payload.courseDate.id,
    })
      .set("embedded", "attributes.time", payload.time)
      .inc("attributes.calendar.version", 1).patch,
  ]
}

function undoPatches(payload: Payload): Array<PatchModel> {
  return [
    Patch.create("course", payload.course.id, "attributes.calendar.dates", {
      id: payload.courseDate.id,
    })
      .set("embedded", "attributes.time", payload.courseDate.attributes.time)
      .inc("attributes.calendar.version", 1).patch,
  ]
}

function narrative(payload: Payload, findFn: FindFn, userId): Narrative {
  let models = defaultCourseModels(payload, findFn, userId)
  let newEvent: CourseEditTime = {
    ...createBlankEvent(),
    root_id: payload.planbookId,
    root_type: "planbook",
    date_plan_scheduled_for: null,

    primary_verb: "edit",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbookId),

    primary_object_type: "course",
    primary_object_id: payload.course.id,
    primary_object_specifier: "time",
    primary_object_id_and_ancestor_ids: [payload.planbookId, payload.course.id],

    adverbial_verb: "editing",

    adverbial_object_type: "day",
    adverbial_object_id: payload.courseDate.id,
    adverbial_object_specifier: "specific",
    adverbial_object_id_and_ancestor_ids: [payload.planbookId, payload.course.id],
  }
  const dateMessage =
    payload.courseDate.type === "course-date-default"
      ? `every ${payload.rotationTitle}`
      : moment(payload.courseDate.attributes.dateString).format("ddd, MMM. D, YYYY")

  return {
    title: "Assigned Time",
    titleForAnalytics: "Course - Assigned Time to Day",
    description: `Set the time of your ${payload.course.attributes.title} class on ${dateMessage}`,
    context: defaultCourseContext(payload, models),
    activity: defaultCourseActivity(payload, models),
    newAnalyticsEvents: [newEvent],
  }
}

const COURSE_SET_ONE_TIME = { patches } as const
export default COURSE_SET_ONE_TIME
